import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, MapPin, Phone, Sparkles, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '@/components/Logo';
import { useToast } from '@/components/ui/use-toast';
import { restaurantApi } from '@/services/api/restaurantApi';
import { useAuth } from '@/services/auth/authContext';
import { getApiUrl } from '@/config/api';
import Cookies from 'js-cookie';

// Helper function to validate navigation URLs and prevent open redirects
const isValidNavigationPath = (path: string): boolean => {
  if (!path) return false;
  
  // Remove any whitespace
  const trimmedPath = path.trim();
  
  // Check for dangerous protocols that could lead to external redirects
  const dangerousProtocols = ['http://', 'https://', 'javascript:', 'data:', 'vbscript:', 'file:', '//'];
  const lowerPath = trimmedPath.toLowerCase();
  
  for (const protocol of dangerousProtocols) {
    if (lowerPath.startsWith(protocol)) {
      console.warn(`Blocked potentially dangerous navigation: ${path}`);
      return false;
    }
  }
  
  // Only allow relative paths that start with '/'
  if (!trimmedPath.startsWith('/')) {
    console.warn(`Invalid navigation path (must start with /): ${path}`);
    return false;
  }
  
  // Additional check: ensure no double slashes that could be interpreted as protocol-relative URLs
  if (trimmedPath.includes('//')) {
    console.warn(`Invalid navigation path (contains //): ${path}`);
    return false;
  }
  
  return true;
};

const RestaurantOnboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Parse user name from social login
  const parseUserName = (fullName: string) => {
    if (!fullName) return { firstName: '', lastName: '' };
    const parts = fullName.trim().split(' ');
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || ''
    };
  };

  const userNames = user?.name ? parseUserName(user.name) : { firstName: '', lastName: '' };
  
  // Debug user state
  console.log('User in onboarding:', user);
  console.log('Parsed names:', userNames);

  // Check for pre-filled data from search flow or frictionless flow
  const prefillData = location.state?.prefillData;
  const selectedMenus = location.state?.selectedMenus;
  const sourceFlow = location.state?.source; // 'search' if coming from search flow
  const searchMessage = location.state?.message; // Message from search flow
  const isFrictionless = location.state?.frictionless;
  const itemsExtracted = location.state?.itemsExtracted;
  const frictionlessRestaurantName = location.state?.restaurantName;
  
  // Debug logging to see what data we receive
  console.log('🏢 RestaurantOnboarding - Location state:', location.state);
  console.log('📝 Prefill data:', prefillData);
  console.log('🎯 Is frictionless:', isFrictionless);
  console.log('🍽️ Restaurant name:', frictionlessRestaurantName);
  console.log('📍 Parsed address:', prefillData?.address);

  // Show message if coming from search with no menus found
  useEffect(() => {
    if (searchMessage) {
      toast({
        title: 'Ready to continue',
        description: searchMessage,
      });
    }
  }, [searchMessage, toast]);
  
  // Fetch restaurant info if we have a session token
  useEffect(() => {
    const fetchRestaurantInfo = async () => {
      const sessionToken = sessionStorage.getItem('preview_session_token') ||
                           localStorage.getItem('extraction_session_token');
      if (sessionToken && !prefillData) {
        try {
          const response = await fetch(getApiUrl(`frictionless-onboarding/get-restaurant-info-session/${sessionToken}`));
          if (response.ok) {
            const data = await response.json();
            if (data.has_info) {
              console.log('📍 Retrieved restaurant info from session:', data);

              // Parse address if needed
              const addressParts = data.restaurant_address ? parseAddress(data.restaurant_address) : { street: '', city: '', state: '', zip: '' };

              // Update form data with retrieved info - only update empty fields
              setFormData(prev => ({
                restaurantName: prev.restaurantName || data.restaurant_name || '',
                cuisineType: prev.cuisineType || data.cuisine_type || '',
                address: prev.address || addressParts.street || data.restaurant_address || '',
                city: prev.city || addressParts.city || data.city || '',
                state: prev.state || addressParts.state || data.state || '',
                zipCode: prev.zipCode || addressParts.zip || data.zip || '',
                phoneNumber: prev.phoneNumber || formatPhoneNumber(data.restaurant_phone || '')
              }));
            }
          }
        } catch (error) {
          console.error('Failed to fetch restaurant info:', error);
        }
      }
    };

    fetchRestaurantInfo();
  }, []);

  // Parse address components from full address
  const parseAddress = (fullAddress: string) => {
    if (!fullAddress) return { street: '', city: '', state: '', zip: '' };
    
    // Try to extract components - format is typically: "123 Main St, City, State ZIP, Country"
    const parts = fullAddress.split(',').map(p => p.trim());
    
    // Google Places format: "Street, City, State ZIP, Country"
    if (parts.length >= 3) {
      const street = parts[0] || '';
      const city = parts[1] || '';
      
      // Handle "State ZIP" or "State ZIP Country"
      let stateZip = parts[2] || '';
      
      // Remove country if present (usually "USA" at the end)
      if (parts.length === 4) {
        stateZip = parts[2];
      }
      
      // Extract ZIP code (5 digits)
      const zipMatch = stateZip.match(/\d{5}/);
      const zip = zipMatch ? zipMatch[0] : '';
      
      // Extract state abbreviation (usually 2 capital letters before ZIP)
      const stateMatch = stateZip.match(/([A-Z]{2})\s+\d{5}/);
      const state = stateMatch ? stateMatch[1] : stateZip.replace(/\d{5}/, '').trim().split(' ')[0] || '';
      
      return { street, city, state, zip };
    }
    
    // Fallback for simpler formats
    const street = parts[0] || '';
    const city = parts[1] || '';
    const state = parts[2] ? parts[2].split(' ')[0] : '';
    const zip = '';
    
    return { street, city, state, zip };
  };
  
  // Format phone number - Google Places returns it as "+1 XXX-XXX-XXXX" or "(XXX) XXX-XXXX"
  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Remove country code if present
    const cleaned = digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;
    // Format as (XXX) XXX-XXXX
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone; // Return original if can't format
  };
  
  const addressParts = prefillData?.address ? parseAddress(prefillData.address) : { street: '', city: '', state: '', zip: '' };

  // Log parsed address for debugging
  if (prefillData) {
    console.log('📬 PrefillData received:', prefillData);
    console.log('📬 Original address:', prefillData.address);
    console.log('📬 Parsed address parts:', addressParts);
    console.log('📬 Phone from prefill:', prefillData.phone);
  }

  const initialFormData = {
    restaurantName: frictionlessRestaurantName || prefillData?.name || '',
    cuisineType: prefillData?.cuisine_type || prefillData?.cuisines?.[0] || '',
    address: addressParts.street || prefillData?.address || '',
    city: addressParts.city || prefillData?.city || '',
    state: addressParts.state || prefillData?.state || '',
    zipCode: addressParts.zip || prefillData?.zip || '',
    phoneNumber: formatPhoneNumber(prefillData?.phone || prefillData?.phone_number || '')
  };
  
  // Log initial form data
  console.log('📋 Initial form data:', initialFormData);
  
  const [formData, setFormData] = useState(initialFormData);
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [termsAndPrivacyAccepted, setTermsAndPrivacyAccepted] = useState(false);

  const cuisineTypes = [
    'American', 'Italian', 'Mexican', 'Chinese', 'Japanese', 
    'Indian', 'Thai', 'French', 'Mediterranean', 'Korean',
    'Vietnamese', 'Greek', 'Spanish', 'Middle Eastern', 'Other'
  ];

  // Try to fetch existing data from Stripe/account
  useEffect(() => {
    const fetchExistingData = async () => {
      // Check if this is adding a new location
      const searchParams = new URLSearchParams(location.search);
      const isAddingLocation = searchParams.get('add_location') === 'true';
      
      try {
        const response = await fetch(getApiUrl('restaurants/profile'), {
          headers: {
            'Authorization': `Bearer ${Cookies.get('auth_token') || ''}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const profile = await response.json();
          
          console.log('🔍 PROFILE CHECK IN useEffect:', {
            restaurant_id: profile.restaurant_id,
            rest_id: profile.rest_id,
            needs_onboarding: profile.needs_onboarding,
            status: profile.status,
            isAddingLocation: isAddingLocation,
            timestamp: new Date().toISOString()
          });
          
          // Check if user already has a restaurant (onboarding completed)
          // IMPORTANT: Skip redirect if status is 'pending' (v2 flow) OR if adding location
          if ((profile.restaurant_id || profile.rest_id) && !profile.needs_onboarding && profile.status !== 'pending' && !isAddingLocation) {
            console.log('🚨 REDIRECTING FROM useEffect - User already onboarded and NOT adding location');
            // User already has a restaurant, check what to do next
            
            // FIRST: Check if they have extraction data to claim
            const hasExtraction = sessionStorage.getItem('extraction_completed') === 'true' || 
                                  sessionStorage.getItem('preview_session_token') !== null ||
                                  localStorage.getItem('pending_extraction') === 'true';
            
            if (hasExtraction) {
              console.log('User already onboarded with extraction data - going to claim page');
              navigate('/restaurant-menu-claim');
              return;
            }
            
            // Otherwise check if they have a menu
            try {
              const menuResponse = await fetch(getApiUrl('restaurants/active-menu'), {
                headers: {
                  'Authorization': `Bearer ${Cookies.get('auth_token') || ''}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (menuResponse.ok) {
                const menu = await menuResponse.json();
                if (menu && menu.menu_id) {
                  // Has active menu, go to dashboard
                  navigate('/restaurant-dashboard');
                } else {
                  // No menu yet, go to menu upload
                  navigate('/restaurant-menu-upload');
                }
              } else {
                // Default to menu upload if can't check
                navigate('/restaurant-menu-upload');
              }
            } catch (error) {
              console.error('Error checking menu status:', error);
              navigate('/restaurant-menu-upload');
            }
            return; // Exit early, user is already onboarded
          }
          
          // Pre-fill phone number if available from Stripe
          if (profile.phone_number && profile.phone_number !== '000-000-0000') {
            setFormData(prev => ({
              ...prev,
              phoneNumber: profile.phone_number
            }));
          }
          // Display owner name if available from Stripe
          if (profile.owner_full_name) {
            console.log('Owner name from Stripe:', profile.owner_full_name);
            // Could display this in a welcome message or pre-fill fields
          }
        }
      } catch (error) {
        console.error('Error fetching existing data:', error);
      }
    };

    fetchExistingData();
  }, [navigate, location.search]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Format phone number as user types
    if (name === 'phoneNumber') {
      // Remove all non-digits
      const digits = value.replace(/\D/g, '');
      
      // Limit to 10 digits
      const limitedDigits = digits.slice(0, 10);
      
      // Format as (XXX) XXX-XXXX
      let formatted = '';
      if (limitedDigits.length > 0) {
        if (limitedDigits.length <= 3) {
          formatted = `(${limitedDigits}`;
        } else if (limitedDigits.length <= 6) {
          formatted = `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
        } else {
          formatted = `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
        }
      }
      
      setFormData({
        ...formData,
        phoneNumber: formatted
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const validateForm = () => {
    if (!formData.restaurantName.trim()) {
      toast({ title: "Error", description: "Restaurant name is required", variant: "destructive" });
      return false;
    }
    if (!formData.cuisineType) {
      toast({ title: "Error", description: "Please select a cuisine type", variant: "destructive" });
      return false;
    }
    if (!formData.address.trim()) {
      toast({ title: "Error", description: "Address is required", variant: "destructive" });
      return false;
    }
    if (!formData.city.trim()) {
      toast({ title: "Error", description: "City is required", variant: "destructive" });
      return false;
    }
    if (!formData.state.trim()) {
      toast({ title: "Error", description: "State is required", variant: "destructive" });
      return false;
    }
    if (!formData.zipCode.trim()) {
      toast({ title: "Error", description: "ZIP code is required", variant: "destructive" });
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      toast({ title: "Error", description: "Phone number is required", variant: "destructive" });
      return false;
    }
    if (!termsAndPrivacyAccepted) {
      toast({ 
        title: "Error", 
        description: "You must accept both Terms of Service and Privacy Policy", 
        variant: "destructive" 
      });
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get plan selection from location.state (passed from plan-selection page)
      // IMPORTANT: Only use location.state, NOT localStorage to avoid stale data
      const subscriptionPlan = location.state?.selectedPlan;
      const billingCycle = location.state?.selectedPlan ? localStorage.getItem('billing_period') || 'monthly' : 'monthly';

      // Don't require subscription for initial restaurant setup
      const isInitialSetup = !subscriptionPlan;

      console.log('📋 ONBOARDING: Initial setup:', isInitialSetup);
      console.log('📋 ONBOARDING: Subscription plan from location.state:', subscriptionPlan);
      console.log('📋 ONBOARDING: location.state:', location.state);
      
      // Submit to API
      const onboardingData: any = {
        restaurant_name: formData.restaurantName,
        cuisine_type: formData.cuisineType,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        phone_number: formData.phoneNumber,
        email_opt_in: emailOptIn,
        terms_accepted: termsAndPrivacyAccepted,
        privacy_accepted: termsAndPrivacyAccepted,
      };

      // Add Google rating if available from prefill data
      if (prefillData?.rating) {
        onboardingData.google_rating = prefillData.rating;
        onboardingData.rating_source = 'google';
      }
      
      // Only include subscription info if available (for adding locations)
      if (subscriptionPlan) {
        onboardingData.subscription_plan = subscriptionPlan;
        onboardingData.billing_cycle = billingCycle;
      }
      
      // Include selected menus if coming from search flow
      if (selectedMenus && selectedMenus.length > 0) {
        onboardingData.selected_menus = selectedMenus;
      }
      
      console.log('📤 ONBOARDING: Sending to API with subscription_plan:', onboardingData.subscription_plan);
      console.log('📤 ONBOARDING: Full payload:', onboardingData);
      console.log('📤 ONBOARDING: Selected menus:', selectedMenus);
      
      // Check if this is adding a new location
      const searchParams = new URLSearchParams(location.search);
      const isAddingLocation = searchParams.get('add_location') === 'true';
      
      // Use the onboard endpoint with add_location parameter when adding locations
      const endpoint = isAddingLocation 
        ? 'restaurants/onboard?add_location=true'
        : 'restaurants/onboard';
      
      const response = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${Cookies.get('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(onboardingData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw { response: { data: error } };
      }
      
      const result = await response.json();
      
      console.log('🔍 ONBOARDING RESPONSE:', result);
      console.log('🔍 result.success:', result.success);
      console.log('🔍 Current timestamp:', new Date().toISOString());
      
      if (result.success) {
        toast({
          title: "Success!",
          description: result.message || "Restaurant information saved!",
        });

        // Clean up plan selection from localStorage after successful use
        if (subscriptionPlan) {
          console.log('🧹 Cleaning up plan selection from localStorage after successful onboarding');
          localStorage.removeItem('subscription_plan');
          localStorage.removeItem('selected_plan');
          // Keep billing_period for Stripe checkout
        }

        // No restaurant ID yet since we're not creating it here
        if (selectedMenus && selectedMenus.length > 0) {
          localStorage.setItem('pending_menus', JSON.stringify(selectedMenus));
        }

        // Preserve extraction data if exists
        const hasExtraction = sessionStorage.getItem('extraction_completed') === 'true' ||
                             sessionStorage.getItem('preview_session_token') !== null;

        if (hasExtraction) {
          // Store in localStorage to survive payment flow
          localStorage.setItem('pending_extraction', 'true');
          const sessionToken = sessionStorage.getItem('preview_session_token');
          const itemsExtracted = sessionStorage.getItem('preview_items_extracted');
          if (sessionToken) localStorage.setItem('extraction_session_token', sessionToken);
          if (itemsExtracted) localStorage.setItem('extraction_items_count', itemsExtracted);
        }
        
        console.log('🚀 NAVIGATING FROM RestaurantOnboarding.tsx');
        console.log('🚀 Build timestamp: 2025-09-11T00:00:00Z'); // Update this with each build
        
        // Check if backend returned a checkout URL (for paid plans)
        if (result.checkout_url && result.requires_payment) {
          console.log('💳 Onboarding with payment - redirecting to Stripe Checkout');
          console.log('💳 Checkout URL:', result.checkout_url);

          toast({
            title: "Redirecting to payment...",
            description: result.message || "Please complete payment to activate your subscription.",
          });

          // Redirect to Stripe Checkout
          window.location.href = result.checkout_url;
          return;
        }

        // Check if adding location or initial onboarding
        if (isAddingLocation) {
          // Adding a new location - validate the next_step from backend response
          const nextStep = result.next_step || '/restaurant-menu-upload';

          // Validate the path before navigation to prevent open redirect
          if (isValidNavigationPath(nextStep)) {
            toast({
              title: "Location Added!",
              description: result.message || "You can now upload the menu for this location.",
            });
            navigate(`${nextStep}?new_location=true`);
          } else {
            // Fallback to safe default if validation fails
            console.error('Invalid navigation path received from backend:', nextStep);
            toast({
              title: "Location Added!",
              description: "You can now upload the menu for this location.",
            });
            navigate('/restaurant-menu-upload?new_location=true');
          }
        } else {
          // Initial onboarding - validate next_step from backend
          if (result.next_step && isValidNavigationPath(result.next_step)) {
            navigate(result.next_step);
          } else if (result.next_step) {
            // Log the invalid path but use safe default
            console.error('Invalid navigation path received from backend:', result.next_step);
            navigate('/plan-selection?onboarding=true');
          } else {
            // Default to plan selection for initial onboarding
            navigate('/plan-selection?onboarding=true');
          }
        }
      }
    } catch (error: any) {
      console.error('Onboarding error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.error('Request data sent:', {
        restaurant_name: formData.restaurantName,
        cuisine_type: formData.cuisineType,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        phone_number: formData.phoneNumber,
        email_opt_in: emailOptIn,
        terms_accepted: termsAndPrivacyAccepted,
        privacy_accepted: termsAndPrivacyAccepted
      });
      
      // Handle validation errors
      let errorMessage = "Failed to complete onboarding. Please try again.";
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // FastAPI validation errors
          errorMessage = error.response.data.detail[0]?.msg || errorMessage;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-dineai-blue relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
      </div>

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
        <motion.div 
          className="h-full bg-dineai-yellow"
          initial={{ width: '0%' }}
          animate={{ width: '33%' }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* Logo at top-left corner */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="absolute top-8 left-8 z-20"
      >
        <Logo />
      </motion.div>

      <div className="flex min-h-screen">
        {/* Left side - Form */}
        <div className="flex-1 flex items-center justify-center px-8 py-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-2xl"
        >

            {/* Onboarding Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl">
            {/* Success Banner for Frictionless Flow */}
            {isFrictionless && (
              <div className="mb-6 p-4 bg-green-500/10 rounded-xl border border-green-500/20">
                <div className="flex items-center gap-3">
                  <div className="bg-green-500/20 p-2 rounded-lg">
                    <Sparkles className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Great news! Your menu is ready</p>
                    <p className="text-white/70 text-sm">
                      We've successfully extracted {itemsExtracted} items from your menu. Complete this form to finish setup.
                    </p>
                  </div>
                </div>
              </div>
            )}

              {/* Header */}
              <div className="mb-6">
                <div className="inline-block bg-dineai-yellow/20 px-3 py-1.5 rounded-full mb-3">
                  <span className="text-dineai-yellow font-medium text-xs flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" />
                    STEP 1 OF 3
                  </span>
                </div>
                
                <h1 className="text-xl md:text-2xl font-bold text-white mb-2">
                  Welcome to{" "}
                  <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                    Dine AI!
                  </span>
                </h1>
                <p className="text-white/70 text-sm">
                  Let's start by getting to know your restaurant
                </p>
                {user && (
                  <p className="text-white/50 text-xs mt-1">
                    Welcome, {user.name || user.email}!
                  </p>
                )}
              </div>

              {/* Form */}
              <div className="space-y-6">

                {/* Row 1: Restaurant Name & Cuisine Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      Restaurant Name <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                      <Input
                        type="text"
                        name="restaurantName"
                        value={formData.restaurantName}
                        onChange={handleInputChange}
                        placeholder="Enter your restaurant name"
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      Cuisine Type <span className="text-red-400">*</span>
                    </label>
                    <Select 
                      value={formData.cuisineType}
                      onValueChange={(value) => setFormData({...formData, cuisineType: value})}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20 text-white h-10">
                        <SelectValue placeholder="Select cuisine type" />
                      </SelectTrigger>
                      <SelectContent>
                        {cuisineTypes.map((cuisine) => (
                          <SelectItem key={cuisine} value={cuisine}>
                            {cuisine}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Row 2: Street Address & Phone */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      Street Address <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                      <Input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="123 Main Street"
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      Phone Number <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                      <Input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4567"
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Row 3: City, State, Zip */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      City <span className="text-red-400">*</span>
                    </label>
                    <Input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="San Francisco"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      State <span className="text-red-400">*</span>
                    </label>
                    <Input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      placeholder="CA"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                      maxLength={2}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      ZIP Code <span className="text-red-400">*</span>
                    </label>
                    <Input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      placeholder="94105"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                      maxLength={5}
                      required
                    />
                  </div>
                </div>

                {/* Legal Checkboxes */}
                <div className="space-y-2 border-t border-white/20 pt-4">
                  {/* Email Marketing Checkbox */}
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="email-optin"
                      checked={emailOptIn}
                      onCheckedChange={(checked) => setEmailOptIn(checked as boolean)}
                      className="mt-0.5 border-white/40 data-[state=checked]:bg-dineai-yellow data-[state=checked]:border-dineai-yellow"
                    />
                    <label 
                      htmlFor="email-optin" 
                      className="text-xs text-white/70 cursor-pointer leading-relaxed"
                    >
                      Yes, I'd like to receive tips, best practices, and promotional offers
                    </label>
                  </div>

                  {/* Combined Terms and Privacy Checkbox */}
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="terms-privacy"
                      checked={termsAndPrivacyAccepted}
                      onCheckedChange={(checked) => setTermsAndPrivacyAccepted(checked as boolean)}
                      className="mt-0.5 border-white/40 data-[state=checked]:bg-dineai-yellow data-[state=checked]:border-dineai-yellow"
                    />
                    <label 
                      htmlFor="terms-privacy" 
                      className="text-xs text-white/70 cursor-pointer leading-relaxed"
                    >
                      I agree to Dine AI's{' '}
                      <a href="/terms" className="text-dineai-yellow hover:underline">
                        Terms of Service
                      </a>
                      {' '}and{' '}
                      <a href="/privacy" className="text-dineai-yellow hover:underline">
                        Privacy Policy
                      </a>
                      <span className="text-red-400 ml-1">*</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6">
                <Button 
                  onClick={handleNext}
                  disabled={isSubmitting}
                  className="w-full bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-3 text-base font-bold rounded-full shadow-lg transform hover:scale-[1.02] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Help text */}
            <p className="text-center text-white/50 text-xs mt-4">
              This information helps us personalize your AI insights
            </p>
          </motion.div>
        </div>

        {/* Right side - Illustration */}
        <div className="flex-1 bg-gradient-to-br from-white/5 to-white/10 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-10 right-10 w-40 h-40 rounded-full bg-dineai-yellow/10 blur-2xl"></div>
            <div className="absolute bottom-10 left-10 w-60 h-60 rounded-full bg-white/5 blur-3xl"></div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative z-10 text-center px-8"
          >
            <img
              src="/rest_main_icon.png"
              alt="Restaurant"
              className="w-80 h-auto mx-auto mb-6"
            />
            <h2 className="text-2xl font-bold text-white mb-3">
              Ready to Transform Your Restaurant?
            </h2>
            <p className="text-white/70 text-base max-w-md mx-auto">
              Join thousands of restaurants using AI to delight customers and boost revenue
            </p>
            
            {/* Feature badges */}
            <div className="flex justify-center gap-4 mt-6">
              <div className="bg-white/10 px-3 py-1.5 rounded-full">
                <span className="text-white/80 text-xs">✨ Smart Recommendations</span>
              </div>
              <div className="bg-white/10 px-3 py-1.5 rounded-full">
                <span className="text-white/80 text-xs">📊 Real-time Analytics</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantOnboarding;